/**
 * Thermal-receipt printing utility for portable printers.
 *
 * Supports the two common roll widths found on Bluetooth / USB thermal
 * printers (Star, Epson TM, Brother, generic 58mm POS printers, etc.).
 *
 *   width=58  → 48mm printable area (~30 chars/line at 12pt mono)
 *   width=80  → 72mm printable area (~42 chars/line at 12pt mono)
 *
 * Strategy: open a clean, blank child window containing a self-contained
 * HTML document with `@page` CSS targeting the chosen roll. The browser
 * print dialog then lets the cashier pick any installed printer — including
 * Bluetooth thermal printers paired through the OS or AirPrint-compatible
 * portable printers.
 *
 * Why not direct ESC/POS bluetooth? It's flaky across browsers and OSes.
 * The print-dialog route works on iPad, iPhone, Android, Mac and Windows
 * without driver installation as long as the printer is paired in the OS.
 */

const ESC = (s = "") =>
    String(s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

const fmtMoney = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const fmtTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit",
    });
};

/**
 * @param {object} order   The full order document.
 * @param {object} opts
 *   @param {"customer"|"kitchen"} [opts.kind="customer"]  Receipt vs prep ticket
 *   @param {58|80} [opts.width=58]                         Roll width in mm
 *   @param {string} [opts.businessName="J & W Deli"]
 *   @param {string} [opts.businessLine="New Haven, CT"]
 *   @param {string} [opts.footer="Gracias por su compra"]
 *   @param {boolean} [opts.autoClose=true]                 Close print window after dialog
 */
export function printReceipt(order, opts = {}) {
    if (!order) return;

    const {
        kind = "customer",
        width = 58,
        businessName = "J & W Deli",
        businessLine = "New Haven, CT",
        footer = "Gracias por su compra",
        autoClose = true,
    } = opts;

    const pageWidthMm = width === 80 ? 80 : 58;
    // Pad printable side margins a bit so labels don't get clipped.
    const fontSize = width === 80 ? "13px" : "12px";

    const items = (order.items || [])
        .map((it) => {
            const name = ESC(it.name || "");
            const qty = it.qty || 1;
            const line = it.line_total ?? (it.price || 0) * qty;
            return `
                <tr>
                    <td class="qty">${qty}×</td>
                    <td class="name">${name}</td>
                    <td class="amt">${fmtMoney(line)}</td>
                </tr>
            `;
        })
        .join("");

    const isKitchen = kind === "kitchen";

    // Customer-facing totals only show on payment receipts.
    const totals = isKitchen
        ? ""
        : `
            <div class="row"><span>Subtotal</span><span>${fmtMoney(order.subtotal)}</span></div>
            ${order.delivery_fee
                ? `<div class="row"><span>Delivery</span><span>${fmtMoney(order.delivery_fee)}</span></div>`
                : ""}
            ${order.discount
                ? `<div class="row"><span>Descuento</span><span>-${fmtMoney(order.discount)}</span></div>`
                : ""}
            <div class="row"><span>Impuesto (${((order.tax_rate || 0.0735) * 100).toFixed(2)}%)</span><span>${fmtMoney(order.tax)}</span></div>
            ${order.tip
                ? `<div class="row"><span>Propina</span><span>${fmtMoney(order.tip)}</span></div>`
                : ""}
            <div class="row total"><span>TOTAL</span><span>${fmtMoney(order.total)}</span></div>
            ${order.payment_method === "cash"
                ? `<div class="row"><span>Efectivo</span><span>${fmtMoney(order.cash_received)}</span></div>
                   <div class="row"><span>Cambio</span><span>${fmtMoney(order.change_due)}</span></div>`
                : ""}
            <div class="row sub"><span>Pago</span><span>${ESC((order.payment_method || "").toUpperCase())}</span></div>
        `;

    const orderId = ESC((order.id || "").slice(-8).toUpperCase());

    const html = `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>${ESC(businessName)} · ${orderId}</title>
<style>
    @page {
        size: ${pageWidthMm}mm auto;
        margin: 3mm 2mm;
    }
    * { box-sizing: border-box; }
    html, body {
        margin: 0;
        padding: 0;
        font-family: ui-monospace, "Menlo", "Consolas", "Courier New", monospace;
        font-size: ${fontSize};
        color: #000;
        background: #fff;
        width: ${pageWidthMm}mm;
        line-height: 1.35;
    }
    .center { text-align: center; }
    .b { font-weight: 800; }
    .xl { font-size: 1.5em; font-weight: 900; letter-spacing: 0.5px; }
    .sm { font-size: 0.85em; }
    .muted { color: #555; }
    .sep {
        border-top: 1px dashed #000;
        margin: 6px 0;
    }
    table.items {
        width: 100%;
        border-collapse: collapse;
        margin: 4px 0;
    }
    table.items td {
        vertical-align: top;
        padding: 2px 0;
    }
    table.items .qty {
        width: 8mm;
        font-weight: 800;
        font-size: ${isKitchen ? "1.4em" : "1em"};
    }
    table.items .name {
        font-weight: ${isKitchen ? "800" : "500"};
        font-size: ${isKitchen ? "1.2em" : "1em"};
        word-break: break-word;
    }
    table.items .amt {
        text-align: right;
        white-space: nowrap;
    }
    .row {
        display: flex;
        justify-content: space-between;
        gap: 4px;
        padding: 1px 0;
    }
    .row.total {
        font-weight: 900;
        font-size: 1.25em;
        border-top: 1px solid #000;
        margin-top: 4px;
        padding-top: 4px;
    }
    .row.sub { font-size: 0.85em; color: #444; }
    .notes {
        margin: 6px 0;
        padding: 4px 6px;
        border: 1px solid #000;
        font-weight: 700;
        font-size: 1.05em;
        text-align: center;
    }
    .badge {
        display: inline-block;
        padding: 2px 8px;
        border: 1px solid #000;
        font-weight: 800;
        margin: 2px 0;
        letter-spacing: 1px;
    }
    @media screen {
        body {
            margin: 16px auto;
            box-shadow: 0 4px 20px rgba(0,0,0,.12);
            padding: 8px;
        }
    }
</style>
</head>
<body>
    <div class="center">
        <div class="xl">${ESC(businessName)}</div>
        <div class="sm muted">${ESC(businessLine)}</div>
        ${isKitchen
            ? `<div class="badge">${ESC((order.fulfillment || "PICKUP").toUpperCase())}${order.channel === "pos" ? " · POS" : ""}</div>`
            : `<div class="sm muted">${ESC(fmtTime(order.created_at))}</div>`}
    </div>

    <div class="sep"></div>
    <div class="row sm"><span>Orden</span><span class="b">#${orderId}</span></div>
    ${order.customer_name
        ? `<div class="row sm"><span>Cliente</span><span class="b">${ESC(order.customer_name)}</span></div>`
        : ""}
    ${order.customer_phone
        ? `<div class="row sm"><span>Tel</span><span>${ESC(order.customer_phone)}</span></div>`
        : ""}
    ${isKitchen ? "" : `<div class="row sm"><span>Tipo</span><span>${ESC((order.fulfillment || "pickup").toUpperCase())}</span></div>`}

    <div class="sep"></div>
    <table class="items">${items}</table>
    <div class="sep"></div>

    ${order.notes
        ? `<div class="notes">${isKitchen ? "★ " : ""}${ESC(order.notes)}${isKitchen ? " ★" : ""}</div>`
        : ""}

    ${totals}

    ${isKitchen
        ? `<div class="center sm muted" style="margin-top:8px">${ESC(fmtTime(order.created_at))}</div>`
        : `<div class="center" style="margin-top:8px">
              <div class="sm">${ESC(footer)}</div>
              <div class="sm muted">jwdeli.com</div>
              <div class="sm" style="margin-top:4px"><b>Sigue tu pedido:</b></div>
              <div class="sm muted" style="word-break:break-all">jwdeli.com/order/${ESC(order.id || "")}</div>
           </div>`}

    <div style="height:14mm"></div>
    <script>
        // Trigger the OS print dialog immediately so the cashier can pick
        // the bluetooth/portable printer and print straight to the roll.
        window.addEventListener("load", function () {
            setTimeout(function () {
                window.focus();
                window.print();
                ${autoClose ? "setTimeout(function(){ window.close(); }, 400);" : ""}
            }, 60);
        });
    </script>
</body>
</html>`;

    // Pop-up blocker friendly: open same-origin window first, then write.
    const w = window.open("", "jw_receipt", "width=420,height=720");
    if (!w) {
        alert(
            "El navegador bloqueó la ventana de impresión.\n" +
            "Permite ventanas emergentes para este sitio e inténtalo de nuevo.",
        );
        return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
}

export default printReceipt;
